import Controller from '@ember/controller';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { action } from '@ember/object';

import ExtApiKeyModel from 'cio/models/ext-api-key';
import ApiCredentialModel from 'cio/models/api-credential';
import WhitelistedIpModel from 'cio/models/whitelisted-ip';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import apiKeyTypeInterface, { ApiCredentialType } from 'cio/utils/api-key-type-interface';
import AnalyticsService from 'cio/services/analytics';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';

const KEY_TYPE_QUERY_DEFAULT_VALUE = ApiCredentialType.Tracking;
const ENVIRONMENT_PAGE_SIZE = 20;

export default class ApiCredentialsController extends Controller {
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;

  // @ts-ignore queryParams in the array format is not typed
  queryParams = [
    {
      keyTypeQP: 'keyType',
    },
  ];

  @tracked credentials: ApiCredentialModel[] | null = null;

  // initializing to "null" to avoid empty value being visible in the URL ("?keyType=")
  // then wrapping with setter/getter to handle default value:
  @tracked keyTypeQP: ApiCredentialType | null = null;

  get keyType() {
    return this.keyTypeQP ?? KEY_TYPE_QUERY_DEFAULT_VALUE;
  }
  set keyType(val: ApiCredentialType) {
    this.keyTypeQP = val === KEY_TYPE_QUERY_DEFAULT_VALUE ? null : val;
  }

  get areCredentialsInitialized() {
    return this.fetchApiCredentialsTask.performCount > 0 && this.credentials != null;
  }

  _lastCreatedAppApiKey!: ExtApiKeyModel;
  _keyPendingDeletion!: ExtApiKeyModel | ApiCredentialModel;

  @computed('keyType')
  get _uiText() {
    return apiKeyTypeInterface[this.keyType].uiText;
  }

  // whitelisted ip
  _createWhitelistedIp = task(async (whitelistedIp: WhitelistedIpModel) => {
    await whitelistedIp.save();

    this.alerts.success(`whitelistedIp.create.success`, `${whitelistedIp.ip} whitelisted`);
  });

  _destroyWhitelistedIp = task(async (whitelistedIp: WhitelistedIpModel) => {
    await whitelistedIp.destroyRecord();

    this.alerts.success(`whitelistedIp.delete.success`, `${whitelistedIp.ip} removed`);
  });

  // app keys
  _saveNewAppApiKey = task(async (key: ExtApiKeyModel) => {
    await key.save();

    this.set('_lastCreatedAppApiKey', key);
  });

  // mixed key types
  _updateCredential = task(async (credential: ExtApiKeyModel & ApiCredentialModel) => {
    let successMessage = `${credential.name} credentials ${credential.get('isNew') ? 'created' : 'updated'}`;

    await credential.save();

    this.alerts.success(`${apiKeyTypeInterface[this.keyType].modelName}.update.success`, successMessage);
  });

  _destroyCredential = task(async (credential: ExtApiKeyModel | ApiCredentialModel) => {
    await credential.destroyRecord();

    this.set('_keyPendingDeletion', null);
    this.alerts.success(
      `${apiKeyTypeInterface[this.keyType].modelName}.delete.success`,
      `${credential.name} credentials deleted`,
    );
  });

  @action
  trackKeysViewed(keyType: string) {
    if (keyType === 'app') {
      this.analytics.track('integration', 'app_api_keys_viewed');
    } else if (keyType === 'tracking') {
      this.analytics.track('integration', 'tracking_api_keys_viewed');
    }
  }
  @action
  createNewCredential() {
    this.analytics.track('integration', 'api_credentials_creation_started', {
      keyType: `${this.keyType.charAt(0).toUpperCase()}${this.keyType.slice(1)}`,
    });
    this.store.createRecord(apiKeyTypeInterface[this.keyType].modelName);
  }
  @action
  createNewAddress() {
    this.store.createRecord('whitelisted-ip');
  }
  @action
  trackKeyCopy() {
    this.analytics.track('integration', 'api_credentials_copied', {
      credentialType: 'App',
      credentialCopied: 'Api Key',
    });
  }

  fetchApiCredentialsTask = task({ restartable: true }, async () => {
    try {
      let page = 0;
      let total = 0;

      do {
        page += 1;
        let credentials = await this.store.query('api_credential', {
          page,
          size: ENVIRONMENT_PAGE_SIZE,
        });

        this.credentials =
          this.credentials == null ? credentials.toArray() : [...this.credentials, ...credentials.toArray()];
        total = (credentials as any).meta?.pagination.total_envs ?? 0;
      } while (page * ENVIRONMENT_PAGE_SIZE < total);
    } catch (error: any) {
      this.credentials = [];
      this.apiErrors.report(error);
    }
  });
}
